import { cloneElement, forwardRef, useId } from 'react';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import { Label } from '../Label/Label.js';
import styles from './Field.module.css';

/** The minimum shape Field needs to be able to clone its control child. Input,
 *  Textarea and Select all satisfy this without any extra work. */
interface FieldControlProps {
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
}

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** The control — an <Input>, <Textarea> or <Select>, cloned with a
   *  generated id and the right aria-* wiring so Field never needs the
   *  consumer to pass htmlFor by hand. */
  children: ReactElement<FieldControlProps>;
  id?: string;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  { label, hint, error, required, children, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div ref={ref} className={cx(styles.field, className)} {...rest}>
      <div className={styles.labelRow}>
        <Label htmlFor={controlId}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </Label>
      </div>
      {cloneElement(children, {
        id: controlId,
        'aria-invalid': error ? 'true' : undefined,
        'aria-describedby': describedBy,
      })}
      {hint && !error && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
});
